// Ross Crossover Solo — Tier 2 vector-fidelity control panel.
//
// Visual constraint: SVG/CSS only. No raster textures. Panel chrome
// (black metal chassis, white-cap buttons, magenta key transitions,
// silver T-bar) is reproduced using CSS gradients + box-shadow.
//
// Hardware reference: docs/sources/equipment/ross-crossover-brochure.pdf
// (Crossover Solo — smallest model in the line, 1 MLE, 10-button
// buses with BLACK MATTE + INPUT 1-8 + SHIFT alternates,
// integrated transition cluster on the right of the same chassis).
//
// Doctrine: ADR-0015 §10 (interactive single-user simulators).
// Fidelity contract: docs/insights/tier-2-vector-fidelity.md.

import { useCallback, useEffect, useRef, useState } from "react";
import MultiviewCO160a from "./multiview-co-160a";
import { HelpShell } from "./help-shell";
import { ScaledSurface } from "./scaled-surface";
import type { CameraFraming } from "./scenario-camera-state";
import {
  CROSSOVER_SOURCES,
  isCleanTake,
  isCrossoverSourceId,
  shortcutFor,
  toMultiviewSource,
  type CrossoverSoloAction,
  type CrossoverSourceId,
} from "./crossover-solo-actions";

export type { CrossoverSoloAction };

const SOURCES = CROSSOVER_SOURCES;
type SourceId = CrossoverSourceId;

// 10 physical bus slots: 0=BLACK MATTE, 1-8=INPUT 1-8, 9=SHIFT.
// Slots without a CO-160a assignment render as grey-cap spares.
const SLOT_MAP = new Map(SOURCES.map((s) => [s.slot, s] as const));
const SLOT_INDICES = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9] as const;

// Hardware printing on each bus button. Top line is the unshifted
// designation, bottom line is the SHIFT alternate. Matches Ross's
// double-row legend on the real plastic caps.
const SLOT_LEGEND: Record<number, { top: string; bottom?: string }> = {
  0: { top: "BLACK", bottom: "MATTE" },
  1: { top: "INPUT 1", bottom: "INPUT 9" },
  2: { top: "INPUT 2", bottom: "INPUT 10" },
  3: { top: "INPUT 3", bottom: "INPUT 11" },
  4: { top: "INPUT 4", bottom: "INPUT 12" },
  5: { top: "INPUT 5" },
  6: { top: "INPUT 6" },
  7: { top: "INPUT 7", bottom: "MEDIA 1" },
  8: { top: "INPUT 8", bottom: "MEDIA 2" },
};

function usePrefersReducedMotion(): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(query.matches);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  return prefersReducedMotion;
}

export interface CrossoverSoloSeatState {
  pgm: SourceId;
  pvw: SourceId;
  keys: string[];
}

export interface CrossoverSoloPanelProps {
  initialState?: { pgm?: string; pvw?: string };
  cameraFraming?: Partial<CameraFraming>;
  onStateChange?: (state: CrossoverSoloSeatState) => void;
  onAction?: (action: CrossoverSoloAction) => void;
}

export default function CrossoverSoloPanel({
  initialState,
  cameraFraming,
  onStateChange,
  onAction,
}: CrossoverSoloPanelProps = {}) {
  const initialPgm: SourceId = isCrossoverSourceId(initialState?.pgm)
    ? initialState!.pgm!
    : "cam1";
  const initialPvw: SourceId = isCrossoverSourceId(initialState?.pvw)
    ? initialState!.pvw!
    : "cam2";

  const [pgm, setPgm] = useState<SourceId>(initialPgm);
  const [pvw, setPvw] = useState<SourceId>(initialPvw);
  // Continuous transition state: when non-null a take is mid-flight.
  //   driver = "auto" → RAF interpolates progress over AUTO_DURATION_MS
  //   driver = "tbar" → operator drives progress via pointer drag
  // On progress >= 1, commit (PGM = transition.to, transition = null).
  type TransitionType = "diss" | "wipe" | "dve";
  interface Transition {
    from: SourceId;
    to: SourceId;
    progress: number;
    type: TransitionType;
    driver: "auto" | "tbar";
    startedAt: number;
  }
  const [transition, setTransition] = useState<Transition | null>(null);
  const [transitionType, setTransitionType] = useState<TransitionType>("diss");
  const [activeKey, setActiveKey] = useState<1 | 2 | 3>(1);
  const [cleanTake, setCleanTake] = useState(true);
  const lastActionTimeRef = useRef(0);
  const lastTbarEmitRef = useRef(0);
  const transitioning = transition !== null;
  const prefersReducedMotion = usePrefersReducedMotion();

  const armPreset = useCallback(
    (id: SourceId) => {
      setPvw(id);
      onAction?.({ kind: "arm", target: id, clean: null, ts_ms: Date.now() });
    },
    [onAction],
  );

  const setProgramDirect = useCallback(
    (id: SourceId) => {
      const now = Date.now();
      const clean = isCleanTake(now, lastActionTimeRef.current, id, pvw);
      lastActionTimeRef.current = now;
      setCleanTake(clean);
      setPgm(id);
      onAction?.({ kind: "cut", target: id, clean, ts_ms: now });
    },
    [pvw, onAction],
  );

  const doCut = useCallback(() => {
    if (transition) return;
    const now = Date.now();
    const clean = isCleanTake(now, lastActionTimeRef.current, pgm, pvw);
    lastActionTimeRef.current = now;
    setCleanTake(clean);
    setPgm(pvw);
    onAction?.({ kind: "cut", target: pvw, clean, ts_ms: now });
  }, [transition, pgm, pvw, onAction]);

  const doAutoTrans = useCallback(() => {
    if (transition) return;
    const now = Date.now();
    const clean = isCleanTake(now, lastActionTimeRef.current, pgm, pvw);
    lastActionTimeRef.current = now;
    setCleanTake(clean);
    onAction?.({ kind: "auto", target: pvw, clean, ts_ms: now });
    if (prefersReducedMotion) {
      setPgm(pvw);
      return;
    }
    setTransition({
      from: pgm,
      to: pvw,
      progress: 0,
      type: transitionType,
      driver: "auto",
      startedAt: now,
    });
  }, [transition, pgm, pvw, transitionType, onAction, prefersReducedMotion]);

  // RAF loop for AUTO-driven transitions. T-bar transitions are driven
  // directly by pointer events and skip this loop.
  useEffect(() => {
    if (!transition || transition.driver !== "auto") return;
    const AUTO_DURATION_MS = 800;
    let rafId = 0;
    const tick = () => {
      const elapsed = Date.now() - transition.startedAt;
      const p = Math.min(1, elapsed / AUTO_DURATION_MS);
      setTransition((cur) =>
        cur && cur.driver === "auto" ? { ...cur, progress: p } : cur,
      );
      if (p < 1) {
        rafId = requestAnimationFrame(tick);
      }
    };
    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [transition]);

  // Commit watcher — fires for both auto and tbar drivers when
  // progress reaches 1.0. T-bar emits its terminal `auto` event here
  // (auto driver already emitted at click).
  useEffect(() => {
    if (!transition || transition.progress < 1) return;
    if (transition.driver === "tbar") {
      const now = Date.now();
      const clean = isCleanTake(
        now,
        lastActionTimeRef.current,
        transition.from,
        transition.to,
      );
      lastActionTimeRef.current = now;
      setCleanTake(clean);
      onAction?.({ kind: "auto", target: transition.to, clean, ts_ms: now });
    }
    setPgm(transition.to);
    setTransition(null);
  }, [transition, onAction]);

  // T-bar drag handlers (mouse + touch via pointer events).
  const tbarTrackRef = useRef<HTMLDivElement | null>(null);
  const tbarDragRef = useRef<{
    pointerId: number;
    startClientY: number;
    startProgress: number;
    trackTop: number;
    trackHeight: number;
  } | null>(null);

  const handleTbarPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      // Don't interrupt an auto transition.
      if (transition?.driver === "auto") return;
      const track = tbarTrackRef.current;
      if (!track) return;
      const rect = track.getBoundingClientRect();
      e.currentTarget.setPointerCapture(e.pointerId);
      e.preventDefault();
      // If no transition in flight, start one bound to the current bus state.
      if (!transition) {
        setTransition({
          from: pgm,
          to: pvw,
          progress: 0,
          type: transitionType,
          driver: "tbar",
          startedAt: Date.now(),
        });
      }
      tbarDragRef.current = {
        pointerId: e.pointerId,
        startClientY: e.clientY,
        startProgress: transition?.progress ?? 0,
        trackTop: rect.top,
        trackHeight: rect.height,
      };
    },
    [transition, pgm, pvw, transitionType],
  );

  const handleTbarPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = tbarDragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      const deltaY = e.clientY - drag.startClientY;
      const newProgress = Math.max(
        0,
        Math.min(1, drag.startProgress + deltaY / drag.trackHeight),
      );
      setTransition((cur) =>
        cur && cur.driver === "tbar" ? { ...cur, progress: newProgress } : cur,
      );
      // Throttle progress events to ~30 Hz.
      const now = Date.now();
      if (now - lastTbarEmitRef.current >= 33) {
        lastTbarEmitRef.current = now;
        onAction?.({ kind: "tbar_progress", value: newProgress, ts_ms: now });
      }
    },
    [onAction],
  );

  const handleTbarPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = tbarDragRef.current;
      if (!drag || drag.pointerId !== e.pointerId) return;
      e.currentTarget.releasePointerCapture(e.pointerId);
      tbarDragRef.current = null;
      // If released mid-pull (not committed and not at zero), emit a partial
      // event so drills can grade "completed the take with a half-pulled bar".
      const cur = transition;
      if (cur && cur.driver === "tbar" && cur.progress > 0 && cur.progress < 1) {
        onAction?.({
          kind: "tbar_partial",
          progress: cur.progress,
          ts_ms: Date.now(),
        });
      }
    },
    [transition, onAction],
  );

  const selectTransitionType = useCallback(
    (kind: "diss" | "wipe" | "dve") => {
      setTransitionType(kind);
      onAction?.({ kind, target: pvw, clean: true, ts_ms: Date.now() });
    },
    [pvw, onAction],
  );

  const fireKey = useCallback(
    (mode: "cut" | "auto", index: 1 | 2 | 3) => {
      onAction?.({
        kind: mode === "cut" ? "key_cut" : "key_auto",
        key_index: index,
        ts_ms: Date.now(),
      });
    },
    [onAction],
  );

  const fireFtb = useCallback(() => {
    onAction?.({ kind: "ftb", ts_ms: Date.now() });
  }, [onAction]);

  const fireDsk = useCallback(() => {
    onAction?.({ kind: "dsk", ts_ms: Date.now() });
  }, [onAction]);

  useEffect(() => {
    onStateChange?.({ pgm, pvw, keys: [] });
  }, [pgm, pvw, onStateChange]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName?.toLowerCase();
      if (tag === "input" || tag === "textarea") return;
      const action = shortcutFor(e.key, e.shiftKey, activeKey);
      if (!action) return;
      e.preventDefault();
      switch (action.kind) {
        case "pgm":
          setProgramDirect(action.target);
          break;
        case "pvw":
          armPreset(action.target);
          break;
        case "cut":
          doCut();
          break;
        case "auto":
          doAutoTrans();
          break;
        case "key_auto":
          fireKey("auto", action.key_index);
          break;
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [setProgramDirect, armPreset, doCut, doAutoTrans, fireKey, activeKey]);

  return (
    <HelpShell panelKind="crossover-solo">
      <style>{PANEL_STYLES}</style>
      <ScaledSurface designWidth={1800}>
        <div className="cs-stage" role="group" aria-label="Ross Crossover Solo control panel">
          <div className="cs-banner" role="status">
            <span>
              <strong>PRACTICING ON:</strong> Ross Crossover Solo · 1 MLE · CO-160a multiview
            </span>
            <span className={`cs-clean ${cleanTake ? "ok" : "bad"}`}>
              {cleanTake ? "Clean take" : "Double-tap or collision"}
            </span>
          </div>

          <div className="cs-mv" data-hotspot-id="multiview">
            <MultiviewCO160a
              pgmSource={toMultiviewSource(pgm)}
              pvwSource={toMultiviewSource(pvw)}
              transition={
                transition
                  ? {
                      fromSource: toMultiviewSource(transition.from),
                      toSource: toMultiviewSource(transition.to),
                      progress: transition.progress,
                      type: transition.type,
                    }
                  : null
              }
              cameraFraming={cameraFraming}
              size="wide"
              ariaLabel={`Multiview — ${pgm.toUpperCase()} on PROGRAM, ${pvw.toUpperCase()} on PREVIEW`}
            />
          </div>

          <div className="cs-panel">
            <div className="cs-panel-modules">
              <ChassisModule
                pgm={pgm}
                pvw={pvw}
                activeKey={activeKey}
                onArm={armPreset}
                onSetPgm={setProgramDirect}
              />
              <TransitionModule
                transitionType={transitionType}
                onSelectTransitionType={selectTransitionType}
                doCut={doCut}
                doAutoTrans={doAutoTrans}
                transitioning={transitioning}
                tbarProgress={transition?.progress ?? 0}
                tbarTrackRef={tbarTrackRef}
                onTbarPointerDown={handleTbarPointerDown}
                onTbarPointerMove={handleTbarPointerMove}
                onTbarPointerUp={handleTbarPointerUp}
                activeKey={activeKey}
                onSelectKey={setActiveKey}
                fireKey={fireKey}
                fireFtb={fireFtb}
                fireDsk={fireDsk}
              />
            </div>

            <p className="cs-shortcuts">
              Shortcuts: 1·2·3·7·8 PGM · q·w·e·u·i PVW · 0/` BLACK · Enter = CUT · Space = AUTO TRANS · Shift+K = Key {activeKey} AUTO
            </p>
          </div>
        </div>
      </ScaledSurface>
    </HelpShell>
  );
}

/* -------------------------------------------------------------------------
 * Left half of the chassis: utility grid, mode cluster, key/aux mode strip,
 * and the three 10-button buses.
 * ----------------------------------------------------------------------- */

function ChassisModule({
  pgm,
  pvw,
  activeKey,
  onArm,
  onSetPgm,
}: {
  pgm: SourceId;
  pvw: SourceId;
  activeKey: 1 | 2 | 3;
  onArm: (id: SourceId) => void;
  onSetPgm: (id: SourceId) => void;
}) {
  return (
    <div className="cs-chassis">
      <div className="cs-brand-left">
        CR<em>OSS</em>OVER <span className="cs-brand-model">SOLO · 1 MLE</span>
      </div>

      <div className="cs-top-row">
        <UtilityGrid />
        <ModeCluster />
      </div>

      <KeyAuxStrip activeKey={activeKey} />

      <div className="cs-bus-stack">
        <BusRow role="key" current={null} onSelect={() => {}} />
        <span className="cs-bus-name">KEY/AUX</span>
        <BusRow role="pgm" current={pgm} onSelect={onSetPgm} />
        <span className="cs-bus-name pgm">PROGRAM</span>
        <BusRow role="pvw" current={pvw} onSelect={onArm} />
        <span className="cs-bus-name pvw">PRESET</span>
        <div className="cs-aux-labels" aria-hidden>
          <span>AUX PGM</span>
          <span>AUX PV</span>
          <span>AUX CLN</span>
        </div>
      </div>
    </div>
  );
}

function UtilityGrid() {
  // Top numeric utility grid. Mirrors the real Crossover front panel:
  // STATUS / OPTIONS / SYSTEM / REF / CONFIG above
  // RESET  / USER    / PERS   / SAVE / LOAD below.
  const labels = [
    { num: "0", lbl: "STATUS", icon: "•" },
    { num: "1", lbl: "OPTIONS", icon: "▢" },
    { num: "2", lbl: "SYSTEM", icon: "1" },
    { num: "3", lbl: "REF", icon: "↺" },
    { num: "4", lbl: "CONFIG", icon: "◆" },
    { num: "5", lbl: "RESET", icon: "↻" },
    { num: "6", lbl: "USER", icon: "U" },
    { num: "7", lbl: "PERS", icon: "P" },
    { num: "8", lbl: "SAVE", icon: "S" },
    { num: "9", lbl: "LOAD", icon: "L" },
  ];
  return (
    <div className="cs-util-grid" aria-hidden>
      {labels.map(({ num, lbl, icon }, i) => (
        <span key={num} className={`cs-util-key ${i === 0 ? "lit" : ""}`}>
          <span className="cs-util-icon">{icon}</span>
          <span className="cs-util-lbl">{lbl}</span>
        </span>
      ))}
    </div>
  );
}

function ModeCluster() {
  return (
    <div className="cs-mode-cluster" aria-hidden>
      <span className="cs-mode-key">STORE</span>
      <span className="cs-mode-key">RECALL</span>
      <span className="cs-mode-key">MENU</span>
      <span className="cs-mode-key">NEXT</span>
    </div>
  );
}

function KeyAuxStrip({ activeKey }: { activeKey: 1 | 2 | 3 }) {
  // The horizontal strip above the buses: KEY TYPE | KEY SEL | AUX SEL.
  // KEY 2 SEL is shown lit green to mirror the lit indicator in the
  // brochure photo; the actual lit selection follows activeKey state.
  return (
    <div className="cs-keyaux-strip">
      <div className="cs-strip-group keytype" aria-hidden>
        <span className="cs-strip-key">
          <span className="cs-strip-top">AUTO</span>
          <span className="cs-strip-bot">SEL</span>
        </span>
        <span className="cs-strip-key">
          <span className="cs-strip-top">CHR</span>
          <span className="cs-strip-bot">KEY</span>
        </span>
        <span className="cs-strip-key">
          <span className="cs-strip-top">DVE</span>
        </span>
      </div>
      <div className="cs-strip-group keysel">
        {[1, 2, 3].map((k) => (
          <span
            key={`ks-${k}`}
            className={`cs-strip-key ${activeKey === k ? "lit-green" : ""}`}
            aria-hidden
          >
            <span className="cs-strip-top">KEY {k}</span>
            <span className="cs-strip-bot">SEL</span>
          </span>
        ))}
      </div>
      <div className="cs-strip-group auxsel" aria-hidden>
        {[1, 2, 3].map((a) => (
          <span key={`a-${a}`} className="cs-strip-key">
            <span className="cs-strip-top">AUX</span>
            <span className="cs-strip-bot">{a}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function BusRow({
  role,
  current,
  onSelect,
}: {
  role: "pgm" | "pvw" | "key";
  current: SourceId | null;
  onSelect: (id: SourceId) => void;
}) {
  const stateClass = role === "pgm" ? "hot" : role === "pvw" ? "armed" : "lit";
  const hotspotId = role === "pgm" ? "pgm-bus" : role === "pvw" ? "pvw-bus" : undefined;
  return (
    <div className={`cs-bus ${role}`} data-hotspot-id={hotspotId}>
      {SLOT_INDICES.map((slot) => {
        if (slot === 9) {
          return (
            <span key="shift" className="cs-bus-btn shift" aria-hidden>
              SHIFT
            </span>
          );
        }
        const legend = SLOT_LEGEND[slot]!;
        const src = SLOT_MAP.get(slot);
        if (!src) {
          return (
            <span key={slot} className="cs-bus-btn spare" aria-hidden>
              <span className="cs-bus-top">{legend.top}</span>
              {legend.bottom && <span className="cs-bus-bot">{legend.bottom}</span>}
            </span>
          );
        }
        const isActive = current === src.id;
        const ariaRole = role === "pgm" ? "Program" : role === "pvw" ? "Preset" : "Key/Aux";
        return (
          <button
            key={src.id}
            type="button"
            className={`cs-bus-btn ${isActive ? stateClass : ""}`}
            onClick={() => onSelect(src.id)}
            aria-label={`${ariaRole}: ${src.label}`}
            aria-pressed={isActive}
            disabled={role === "key"}
          >
            <span className="cs-bus-top">{legend.top}</span>
            {legend.bottom && <span className="cs-bus-bot">{legend.bottom}</span>}
          </button>
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------
 * Right half of the chassis: joystick + LCD + knobs + transition cluster
 * + T-bar fader + KEY 1/2/3 TRANS column.
 * ----------------------------------------------------------------------- */

function TransitionModule({
  transitionType,
  onSelectTransitionType,
  doCut,
  doAutoTrans,
  transitioning,
  tbarProgress,
  tbarTrackRef,
  onTbarPointerDown,
  onTbarPointerMove,
  onTbarPointerUp,
  activeKey,
  onSelectKey,
  fireKey,
  fireFtb,
  fireDsk,
}: {
  transitionType: "diss" | "wipe" | "dve";
  onSelectTransitionType: (k: "diss" | "wipe" | "dve") => void;
  doCut: () => void;
  doAutoTrans: () => void;
  transitioning: boolean;
  tbarProgress: number;
  tbarTrackRef: React.MutableRefObject<HTMLDivElement | null>;
  onTbarPointerDown: (e: React.PointerEvent<HTMLDivElement>) => void;
  onTbarPointerMove: (e: React.PointerEvent<HTMLDivElement>) => void;
  onTbarPointerUp: (e: React.PointerEvent<HTMLDivElement>) => void;
  activeKey: 1 | 2 | 3;
  onSelectKey: (k: 1 | 2 | 3) => void;
  fireKey: (mode: "cut" | "auto", index: 1 | 2 | 3) => void;
  fireFtb: () => void;
  fireDsk: () => void;
}) {
  return (
    <div className="cs-trans-mod">
      <div className="cs-brand-right">ROSS</div>
      <span className="cs-usb-tag" aria-hidden>USB 2.0</span>

      <div className="cs-tm-body">
        <div className="cs-tm-left">
          <div className="cs-pos-block" aria-hidden data-hotspot-id="joystick">
            <span className="cs-pos-base">
              <span className="cs-pos-stick" />
            </span>
          </div>

          <div className="cs-lcd-block" aria-hidden>
            <div className="cs-lcd-row">
              <span>5.4%</span>
              <span>69.0%</span>
              <span>31.2%</span>
            </div>
            <div className="cs-lcd-row sub">
              <span>Clip</span>
              <span>Gain</span>
              <span>Transp</span>
            </div>
          </div>

          <div className="cs-knob-row" aria-hidden>
            {[12, -22, 40].map((deg, i) => (
              <div key={i} className="cs-knob-wrap">
                <span className="cs-knob" style={{ transform: `rotate(${deg}deg)` }} />
                <span className="cs-knob-lbl">PUSH</span>
              </div>
            ))}
          </div>

          <div className="cs-led-bar" aria-hidden>
            <span className="cs-led-dot" />
          </div>

          <div className="cs-trans-row">
            <span className="cs-strip-key lit-green" aria-hidden>
              <span className="cs-strip-top">BKGD</span>
            </span>
            {[1, 2, 3].map((k) => (
              <button
                key={`nt-${k}`}
                type="button"
                className={`cs-strip-key ${activeKey === k ? "lit-green" : ""}`}
                onClick={() => onSelectKey(k as 1 | 2 | 3)}
                aria-pressed={activeKey === k}
                aria-label={`Next transition: KEY ${k}`}
              >
                <span className="cs-strip-top">KEY {k}</span>
              </button>
            ))}
          </div>

          <div className="cs-trans-row three" data-hotspot-id="trans-type">
            {(["diss", "wipe", "dve"] as const).map((t) => (
              <button
                key={t}
                type="button"
                className={`cs-strip-key ${transitionType === t ? "lit-green" : ""}`}
                onClick={() => onSelectTransitionType(t)}
                aria-pressed={transitionType === t}
                aria-label={`Trans type ${t.toUpperCase()}`}
              >
                <span className="cs-strip-top">{t.toUpperCase()}</span>
              </button>
            ))}
          </div>

          <div className="cs-take-row">
            <button
              type="button"
              className="cs-take-btn cut"
              data-hotspot-id="cut"
              onClick={doCut}
              aria-label="CUT"
              style={{ opacity: transitioning ? 0.6 : 1 }}
            >
              CUT
            </button>
            <button
              type="button"
              className="cs-take-btn auto"
              data-hotspot-id="auto-trans"
              onClick={doAutoTrans}
              aria-label="AUTO TRANS"
              style={{ opacity: transitioning ? 0.6 : 1 }}
            >
              AUTO<br />TRANS
            </button>
          </div>
        </div>

        <div className="cs-fader-col">
          <div className="cs-fader-led" aria-hidden />
          <div
            ref={tbarTrackRef}
            className="cs-fader-track"
            data-hotspot-id="t-bar"
            role="slider"
            aria-label="Manual transition T-bar"
            aria-valuenow={Math.round(tbarProgress * 100)}
            aria-valuemin={0}
            aria-valuemax={100}
            tabIndex={0}
            onPointerDown={onTbarPointerDown}
            onPointerMove={onTbarPointerMove}
            onPointerUp={onTbarPointerUp}
            onPointerCancel={onTbarPointerUp}
            style={{ touchAction: "none", cursor: "grab" }}
          >
            <div
              className="cs-fader-handle"
              style={{ top: `calc(${tbarProgress * 100}% - 13px)` }}
              aria-hidden
            />
          </div>
        </div>

        <div className="cs-keytrans-col" data-hotspot-id="key-trans-col">
          {[1, 2, 3].map((idx) => (
            <button
              key={`kt-${idx}`}
              type="button"
              className={`cs-kt-btn ${idx === 2 ? "lit-red" : ""}`}
              onClick={() => fireKey("auto", idx as 1 | 2 | 3)}
              aria-label={`Key ${idx} auto trans`}
            >
              KEY {idx}<br />TRANS
            </button>
          ))}
          <button
            type="button"
            className="cs-kt-btn small"
            data-hotspot-id="ftb"
            onClick={fireFtb}
            aria-label="Fade to black"
          >
            FTB
          </button>
          <button
            type="button"
            className="cs-kt-btn small"
            data-hotspot-id="dsk"
            onClick={fireDsk}
            aria-label="Down stream key"
          >
            DSK
          </button>
        </div>
      </div>
    </div>
  );
}

const PANEL_STYLES = `
/* Stage authored at design width inside <ScaledSurface>; viewport
   media queries are not used here. All proportions stay locked. */
.cs-stage {
  background: linear-gradient(180deg, #08090d 0%, #040509 100%);
  padding: 18px;
  border-radius: 14px;
  color: #e7e8ec;
  font-family: -apple-system, "SF Pro Text", system-ui, sans-serif;
  box-shadow: 0 40px 80px -30px rgba(0,0,0,0.9);
  display: grid;
  grid-template-columns: minmax(0, 1fr) 760px;
  grid-template-areas:
    "banner banner"
    "panel  mv";
  column-gap: 18px;
  row-gap: 14px;
  align-items: start;
}
.cs-mv { grid-area: mv; align-self: start; }

.cs-banner {
  grid-area: banner;
  background: linear-gradient(90deg, rgba(255,191,71,0.12) 0%, rgba(255,191,71,0.2) 50%, rgba(255,191,71,0.12) 100%);
  border: 1px solid rgba(255,191,71,0.4);
  border-radius: 6px;
  padding: 10px 14px;
  display: flex; align-items: center; justify-content: space-between;
  font-size: 11px; gap: 12px;
}
.cs-banner strong { letter-spacing: 0.5px; }
.cs-clean {
  padding: 4px 10px;
  border-radius: 14px;
  font-size: 10px; font-weight: 700; letter-spacing: 0.5px;
  border: 1px solid rgba(255,255,255,0.18);
}
.cs-clean.ok { color: #2ade67; border-color: rgba(42,222,103,0.5); background: rgba(42,222,103,0.08); }
.cs-clean.bad { color: #ff5566; border-color: rgba(255,42,60,0.5); background: rgba(255,42,60,0.08); }

/* Single matte-black chassis. The bus chassis (left) and transition
   cluster (right) are visually integrated on real hardware. */
.cs-panel {
  grid-area: panel;
  background:
    radial-gradient(ellipse at 30% 0%, rgba(80,82,90,0.18) 0%, transparent 60%),
    linear-gradient(180deg, #1a1c22 0%, #0d0e12 60%, #08090b 100%);
  border-radius: 8px;
  padding: 22px 24px 18px;
  border: 1px solid #2a2d36;
  box-shadow:
    inset 0 1px 0 rgba(255,255,255,0.04),
    inset 0 -1px 0 rgba(0,0,0,0.8),
    0 12px 28px -8px rgba(0,0,0,0.7);
  position: relative;
}

.cs-panel-modules {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 28px;
  align-items: start;
}

/* ===== LEFT MODULE (bus chassis) ====================================== */
.cs-chassis {
  display: flex; flex-direction: column; gap: 12px;
  position: relative; padding-top: 10px;
  min-width: 0;
}
.cs-brand-left {
  font-size: 14px; letter-spacing: 4px; font-weight: 300; color: #e7e8ec;
  margin-bottom: 4px;
}
.cs-brand-left em {
  font-style: italic; font-weight: 200; letter-spacing: 2px; color: #c8cad0; padding: 0 1px;
}
.cs-brand-model {
  font-size: 9px; letter-spacing: 2px; color: #c47832; font-weight: 700; margin-left: 10px;
}

.cs-top-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 130px;
  gap: 14px;
  align-items: start;
}

.cs-util-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  grid-template-rows: repeat(2, auto);
  gap: 5px;
}
.cs-util-key {
  aspect-ratio: 1.05/1;
  background: linear-gradient(180deg, #f4f4f6 0%, #c8cad0 100%);
  border: 1px solid #9ea0a6;
  border-radius: 3px;
  color: #1a1c22;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 3px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.7);
  min-width: 0;
}
.cs-util-key.lit {
  background: linear-gradient(180deg, #3aea77 0%, #1aa94f 100%);
  border-color: #4aef85; color: #0b0c10;
  box-shadow: 0 0 6px -1px rgba(42,222,103,0.7), inset 0 1px 0 rgba(255,255,255,0.3);
}
.cs-util-icon { font-size: 10px; line-height: 1; opacity: 0.7; margin-bottom: 1px; }
.cs-util-lbl { font-size: 6.5px; letter-spacing: 0.4px; line-height: 1; font-weight: 700; }

.cs-mode-cluster {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  grid-template-rows: repeat(2, 1fr);
  gap: 5px;
  height: 100%;
}
.cs-mode-key {
  background: linear-gradient(180deg, #f4f4f6 0%, #c8cad0 100%);
  border: 1px solid #9ea0a6;
  border-radius: 3px;
  color: #1a1c22;
  display: flex; align-items: center; justify-content: center;
  font-size: 8px; font-weight: 800; letter-spacing: 0.6px;
  padding: 4px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.7);
}

/* Horizontal mode strip ------------------------------------------------- */
.cs-keyaux-strip {
  display: grid;
  grid-template-columns: 3fr 3fr 3fr;
  gap: 14px;
  margin-top: 4px;
  padding-top: 8px;
  border-top: 1px solid rgba(255,255,255,0.05);
}
.cs-strip-group {
  display: grid; gap: 5px;
  grid-template-columns: repeat(3, 1fr);
}
.cs-strip-key {
  aspect-ratio: 1.1/1;
  background: linear-gradient(180deg, #f4f4f6 0%, #c8cad0 100%);
  border: 1px solid #9ea0a6;
  border-radius: 3px;
  color: #1a1c22;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 4px 2px;
  font-weight: 800; line-height: 1;
  box-shadow: 0 1px 2px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.7);
  min-width: 0;
}
button.cs-strip-key { cursor: pointer; }
button.cs-strip-key:focus-visible { outline: 2px solid #ffbf47; outline-offset: 1px; }
.cs-strip-key.lit-green {
  background: linear-gradient(180deg, #3aea77 0%, #1aa94f 100%);
  border-color: #4aef85; color: #0b0c10;
  box-shadow: 0 0 6px -1px rgba(42,222,103,0.65), inset 0 1px 0 rgba(255,255,255,0.3);
}
.cs-strip-top { font-size: 7px; letter-spacing: 0.4px; }
.cs-strip-bot { font-size: 6px; letter-spacing: 0.4px; opacity: 0.85; margin-top: 2px; }

/* Bus stack ------------------------------------------------------------- */
.cs-bus-stack {
  display: flex; flex-direction: column; gap: 4px;
  margin-top: 6px;
}
.cs-bus {
  display: grid;
  grid-template-columns: repeat(10, minmax(0, 1fr));
  gap: 5px;
}
.cs-bus-btn {
  aspect-ratio: 0.78/1;
  border-radius: 3px;
  background: linear-gradient(180deg, #f4f4f6 0%, #c8cad0 100%);
  border: 1px solid #9ea0a6;
  color: #1a1c22;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 4px 1px; min-width: 0;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.7), 0 1px 2px rgba(0,0,0,0.5);
  cursor: pointer;
  font-weight: 700; line-height: 1;
  position: relative;
}
button.cs-bus-btn:focus-visible { outline: 2px solid #ffbf47; outline-offset: 1px; z-index: 2; }
button.cs-bus-btn:disabled { cursor: not-allowed; }
.cs-bus-top { font-size: 7px; letter-spacing: 0.3px; }
.cs-bus-bot { font-size: 6px; letter-spacing: 0.3px; margin-top: 2px; opacity: 0.78; }
.cs-bus-btn.spare {
  cursor: default;
  /* Same cap, but the SHIFT alternate lets us hint the slot is unused
     for CO-160a — visually identical to the real hardware's printed
     legend (slot is physically present, just unassigned in our config). */
}
.cs-bus-btn.shift {
  background: linear-gradient(180deg, #2a2d36 0%, #14161a 100%);
  color: #c8cad0; border-color: #4a4d56;
  font-size: 9px; letter-spacing: 1.5px; font-weight: 800;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.05), 0 1px 2px rgba(0,0,0,0.5);
}
.cs-bus-btn.hot {
  background: linear-gradient(180deg, #ff3848 0%, #a81222 100%);
  color: #fff; border-color: #ff5566;
  box-shadow: 0 0 9px -1px rgba(255,42,60,0.85), inset 0 1px 0 rgba(255,255,255,0.2);
}
.cs-bus-btn.armed {
  background: linear-gradient(180deg, #3aea77 0%, #1aa94f 100%);
  color: #0b0c10; border-color: #4aef85;
  box-shadow: 0 0 8px -1px rgba(42,222,103,0.8), inset 0 1px 0 rgba(255,255,255,0.2);
}
.cs-bus-btn.lit {
  background: linear-gradient(180deg, #ff7a3a 0%, #b04018 100%);
  color: #fff; border-color: #ff9354;
  box-shadow: 0 0 8px -1px rgba(255,122,58,0.7), inset 0 1px 0 rgba(255,255,255,0.2);
}

.cs-bus-name {
  text-align: center;
  font-size: 7px; letter-spacing: 3px; color: #888b94; font-weight: 700;
  padding: 1px 0 2px;
}
.cs-bus-name.pgm { color: #ff5566; }
.cs-bus-name.pvw { color: #2ade67; }

.cs-aux-labels {
  display: grid; grid-template-columns: repeat(3, 1fr);
  gap: 5px; padding: 2px 0 0;
  width: 30%;
}
.cs-aux-labels span {
  text-align: center;
  font-size: 6px; letter-spacing: 1.5px; color: #888b94; font-weight: 700;
}

/* ===== RIGHT MODULE (transition cluster) ============================== */
.cs-trans-mod {
  position: relative;
  padding-top: 10px;
  display: flex; flex-direction: column; gap: 8px;
  min-width: 0;
}
.cs-brand-right {
  position: absolute; top: 0; right: 0;
  font-size: 16px; letter-spacing: 3px; font-weight: 800; color: #e7e8ec; font-style: italic;
}
.cs-brand-right::before {
  content: ""; display: inline-block;
  width: 28px; height: 6px; background: #d12028;
  border-radius: 1px; margin-right: 6px; vertical-align: middle;
  transform: skewX(-18deg);
}
.cs-usb-tag {
  position: absolute; top: 26px; right: 0;
  font-size: 6px; letter-spacing: 1.5px; color: #666973; font-weight: 700;
}

.cs-tm-body {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 36px 64px;
  gap: 10px;
  align-items: stretch;
  margin-top: 28px;
}

.cs-tm-left {
  display: flex; flex-direction: column; gap: 8px; min-width: 0;
}

.cs-pos-block {
  display: flex; align-items: center; justify-content: center;
  padding: 6px 0;
}
.cs-pos-base {
  width: 66px; height: 66px; border-radius: 50%;
  background:
    radial-gradient(circle at 35% 30%, #4a4d56 0%, #1a1c22 50%, #07080b 100%);
  border: 2px solid #4a4d56; position: relative; display: block;
  box-shadow:
    inset 0 -3px 6px rgba(0,0,0,0.8),
    inset 0 2px 4px rgba(255,255,255,0.06),
    0 1px 2px rgba(0,0,0,0.6);
}
.cs-pos-stick {
  position: absolute; top: 50%; left: 50%;
  transform: translate(-50%, -60%); width: 9px; height: 22px;
  background: linear-gradient(180deg, #d8d8dc 0%, #888 50%, #444 100%);
  border-radius: 2px;
  box-shadow: 0 2px 3px rgba(0,0,0,0.6);
}

.cs-lcd-block {
  background: #0a2818; color: #3ae877;
  font-family: "SF Mono", ui-monospace, monospace;
  padding: 5px 8px; border-radius: 2px;
  border: 1px solid #1a4a2a;
  box-shadow: inset 0 1px 3px rgba(0,0,0,0.7);
}
.cs-lcd-row {
  display: grid; grid-template-columns: repeat(3, 1fr);
  font-size: 10px; line-height: 1.2;
}
.cs-lcd-row.sub { font-size: 8px; opacity: 0.85; margin-top: 1px; }

.cs-knob-row {
  display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;
  padding: 4px 0;
}
.cs-knob-wrap { display: flex; flex-direction: column; align-items: center; gap: 2px; }
.cs-knob {
  width: 28px; height: 28px; border-radius: 50%;
  background:
    radial-gradient(circle at 35% 25%, #c8cad0 0%, #6a6c74 50%, #2a2d36 100%);
  border: 1px solid #aaacb4; display: block; position: relative;
  box-shadow: inset 0 -2px 3px rgba(0,0,0,0.5), inset 0 1px 1px rgba(255,255,255,0.3), 0 1px 2px rgba(0,0,0,0.6);
}
.cs-knob::before {
  content: ""; position: absolute; top: 1px; left: 50%;
  transform: translateX(-50%); width: 2px; height: 7px;
  background: #c47832; border-radius: 1px;
}
.cs-knob-lbl { font-size: 6px; letter-spacing: 1px; color: #666973; font-weight: 700; }

.cs-led-bar {
  height: 6px;
  background: linear-gradient(180deg, #14161a 0%, #07080b 100%);
  border: 1px solid #1a1c22;
  border-radius: 1px;
  position: relative;
  box-shadow: inset 0 1px 2px rgba(0,0,0,0.7);
}
.cs-led-dot {
  position: absolute; top: 50%; left: 38%;
  width: 6px; height: 4px; background: #ff2a3c;
  border-radius: 1px; transform: translateY(-50%);
  box-shadow: 0 0 4px rgba(255,42,60,0.9);
}

.cs-trans-row {
  display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px;
}
.cs-trans-row.three { grid-template-columns: repeat(3, 1fr); }

.cs-take-row {
  display: grid; grid-template-columns: 1fr 1.4fr; gap: 6px;
  margin-top: 2px;
}
.cs-take-btn {
  border-radius: 4px;
  padding: 11px 4px; font-size: 11px; font-weight: 800; letter-spacing: 0.5px;
  cursor: pointer; line-height: 1.05;
  border: 1px solid #9ea0a6;
  box-shadow: 0 1px 2px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.5);
}
.cs-take-btn.cut {
  background: linear-gradient(180deg, #f4f4f6 0%, #c8cad0 100%);
  color: #1a1c22;
}
.cs-take-btn.auto {
  background: linear-gradient(180deg, #ff3848 0%, #a81222 100%);
  color: #fff; border-color: #ff5566;
  box-shadow: 0 0 7px -1px rgba(255,42,60,0.7), inset 0 1px 0 rgba(255,255,255,0.2);
}
.cs-take-btn:focus-visible { outline: 2px solid #ffbf47; outline-offset: 1px; }

/* T-bar fader column (vertical strip in the middle of the cluster). */
.cs-fader-col {
  background: linear-gradient(180deg, #08090b 0%, #050608 100%);
  border: 1px solid #1a1c22;
  border-radius: 3px;
  padding: 8px 4px;
  display: flex; flex-direction: column; align-items: center; gap: 6px;
  box-shadow: inset 0 2px 6px rgba(0,0,0,0.8);
  min-width: 0; align-self: stretch;
}
.cs-fader-led {
  width: 4px; height: 10px;
  background: linear-gradient(180deg, #1aa94f 0%, #3ae877 100%);
  border-radius: 1px;
  box-shadow: 0 0 4px rgba(58,232,119,0.6);
}
.cs-fader-track {
  width: 5px; flex: 1;
  background: linear-gradient(90deg, #1a1c22 0%, #0a0b0e 100%);
  border-radius: 2px; position: relative; border: 1px solid #15171c;
  margin: 4px 0; min-height: 200px;
}
.cs-fader-handle {
  position: absolute; left: -14px; right: -14px; height: 26px;
  background:
    linear-gradient(180deg, #d8d8dc 0%, #6a6c74 50%, #2a2d36 100%);
  border: 1px solid #888a92; border-radius: 2px;
  box-shadow: 0 4px 6px rgba(0,0,0,0.8), inset 0 1px 0 rgba(255,255,255,0.4);
  pointer-events: none;
}
.cs-fader-track:active { cursor: grabbing !important; }
.cs-fader-track:focus-visible { outline: 2px solid #ffbf47; outline-offset: 2px; }
.cs-fader-handle::after {
  content: ""; position: absolute; left: 4px; right: 4px; top: 50%;
  height: 2px; background: rgba(0,0,0,0.6); border-radius: 1px;
  transform: translateY(-50%);
}

/* KEY 1/2/3 TRANS column (right of fader). */
.cs-keytrans-col {
  display: grid; grid-auto-rows: 1fr; gap: 5px;
  align-self: stretch;
}
.cs-kt-btn {
  border-radius: 3px;
  background: linear-gradient(180deg, #f4f4f6 0%, #c8cad0 100%);
  color: #1a1c22; border: 1px solid #9ea0a6;
  font-size: 8px; font-weight: 800; letter-spacing: 0.3px;
  padding: 8px 2px; line-height: 1.15; cursor: pointer; text-align: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.5);
}
.cs-kt-btn:focus-visible { outline: 2px solid #ffbf47; outline-offset: 1px; }
.cs-kt-btn.lit-red {
  background: linear-gradient(180deg, #ff3848 0%, #a81222 100%);
  color: #fff; border-color: #ff5566;
  box-shadow: 0 0 7px -1px rgba(255,42,60,0.7), inset 0 1px 0 rgba(255,255,255,0.2);
}
.cs-kt-btn.small { padding: 5px 2px; font-size: 7px; }

.cs-shortcuts {
  margin-top: 16px; text-align: center; font-size: 10px;
  color: #666973; letter-spacing: 0.4px;
}
`;
